import { createAsyncThunk } from '@reduxjs/toolkit'
import { ErrorStore, ListPaymentLinksResponse } from '@types'
import {
  STORE_PAYMENT,
  STORE_PAYMENT_LIST_PAGINATED_PAYMENT_LINKS,
  STORE_STATUS_ERROR,
  STORE_STATUS_FINISHED,
  STORE_STATUS_INITIALIZED,
  STORE_STATUS_LOADING,
} from 'constants/store'
import { Payload } from 'stores'
import {
  createGenericSlice,
  GenericState,
  GetRejectValue,
} from 'stores/generics'

type PaginatedListPaymentLinksState = {
  list: ListPaymentLinksResponse
  shouldRefetch: boolean
}

type State = GenericState<PaginatedListPaymentLinksState>

export const initialState: State = {
  data: {
    shouldRefetch: false,
    list: {
      total: 0,
      payment_links: [],
    },
  },
  status: STORE_STATUS_INITIALIZED,
  error: null,
}

export const fetchPaginatedListPaymentLinks = createAsyncThunk<
  PaginatedListPaymentLinksState,
  Promise<ListPaymentLinksResponse>,
  Payload
>(
  STORE_PAYMENT_LIST_PAGINATED_PAYMENT_LINKS,
  async (promise: Promise<ListPaymentLinksResponse>, { rejectWithValue }) => {
    try {
      const response = await promise

      return {
        list: response,
        shouldRefetch: false,
      }
    } catch (e: unknown) {
      const { code, message } = e as ErrorStore
      return rejectWithValue({ code, message })
    }
  }
)

const userSlice = createGenericSlice({
  name: STORE_PAYMENT,
  initialState,
  reducers: {
    refetch: (state) => {
      return {
        ...state,
        data: {
          list: state.data.list,
          shouldRefetch: true,
        },
        status: STORE_STATUS_LOADING,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaginatedListPaymentLinks.pending, (state) => {
      state.status = STORE_STATUS_LOADING
    })
    builder.addCase(
      fetchPaginatedListPaymentLinks.fulfilled,
      (state, { payload }) => {
        state.error = null
        state.status = STORE_STATUS_FINISHED
        state.data = {
          list: {
            total: payload.list.total,
            payment_links: [
              ...state.data.list.payment_links,
              ...payload.list.payment_links,
            ],
          },
          shouldRefetch: payload.shouldRefetch,
        }
      }
    )
    builder.addCase(
      fetchPaginatedListPaymentLinks.rejected,
      (state, { payload }) => {
        const { code, message, details } = (payload || {}) as GetRejectValue
        state.data = initialState.data
        state.status = STORE_STATUS_ERROR
        state.error = {
          name: code,
          message: message,
          details: details,
        }
      }
    )
  },
})

const { actions, reducer } = userSlice

const { clear, refetch } = actions

export { clear, refetch }

export default reducer
